import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Stack,
  useTheme,
  alpha,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Drawer,
  Skeleton,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { useUserContext } from '@/contexts/userContext';
import McpToolViewer from '@/components/mcp/mcpToolViewer';

interface MarketplaceItem {
  id: string;
  type: 'blueprint' | 'dataSourceDef' | 'agentDef' | 'userAppDef';
  name: string;
  description: string;
  icon: string;
  metadata: {
    domains: string[];
    tech: string[];
  };
  original: any;
}

const MarketplacePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const apiContext = useApiContext();
  const notificationContext = useNotificationContext();
  const userContext = useUserContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Tab category filter
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  // Selected filter domains & tech lists (multi-select)
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedTech, setSelectedTech] = useState<string[]>([]);

  // Sorting and Bookmarks
  const [sortBy, setSortBy] = useState<string>('instances');
  const [showBookmarksOnly, setShowBookmarksOnly] = useState<boolean>(false);
  const [bookmarks, setBookmarks] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('minotii_marketplace_bookmarks');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Track active deployed instances count for each definition ID
  const [instanceCounts, setInstanceCounts] = useState<Record<string, number>>({});

  // Selected item for slide-out detail Drawer
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);

  // Expanded states for 2-column checklists
  const [expandedDomains, setExpandedDomains] = useState<boolean>(false);
  const [expandedTech, setExpandedTech] = useState<boolean>(false);

  // List of domains & technologies for sidebar filters dynamically computed
  const domainsList = useMemo(() => {
    const domainsSet = new Set<string>();
    items.forEach((item) => {
      const domains = item.metadata?.domains;
      if (Array.isArray(domains)) {
        domains.forEach((d) => {
          if (d) domainsSet.add(d);
        });
      }
    });
    return Array.from(domainsSet).sort();
  }, [items]);

  const techList = useMemo(() => {
    const techSet = new Set<string>();
    items.forEach((item) => {
      const tech = item.metadata?.tech;
      if (Array.isArray(tech)) {
        tech.forEach((t) => {
          if (t) techSet.add(t);
        });
      }
    });
    return Array.from(techSet).sort();
  }, [items]);

  useEffect(() => {
    fetchMarketplaceItems();
    
    // Support direct search linking
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
    }
    const categoryParam = params.get('category');
    if (categoryParam) {
      setActiveCategory(categoryParam);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('minotii_marketplace_bookmarks', JSON.stringify(bookmarks));
  }, [bookmarks]);

  const fetchMarketplaceItems = async () => {
    try {
      setLoading(true);
      const data = (await apiContext.searchMarketplace()) as any;
      setItems(Array.isArray(data) ? data : (data?.data || []));

      // Query active instances counts for each resource definition
      if (userContext?.user) {
        try {
          const [pipelines, dataSources, agents, userApps] = await Promise.all([
            apiContext.getPipelineInstances(undefined, undefined, false),
            apiContext.getDataSourceInstances(false, false),
            apiContext.getAgentInstances(false, false),
            apiContext.getUserAppInstances(false, false),
          ]);

          const counts: Record<string, number> = {};

          // 1. Count pipelines by blueprintId
          if (Array.isArray(pipelines)) {
            pipelines.forEach((p: any) => {
              if (p.blueprintId) {
                counts[p.blueprintId] = (counts[p.blueprintId] || 0) + 1;
              }
            });
          }

          // 2. Count data sources by dataSourceDefId
          if (Array.isArray(dataSources)) {
            dataSources.forEach((ds: any) => {
              if (ds.dataSourceDefId) {
                counts[ds.dataSourceDefId] = (counts[ds.dataSourceDefId] || 0) + 1;
              }
            });
          }

          // 3. Count agents by agentDefId
          if (Array.isArray(agents)) {
            agents.forEach((ag: any) => {
              if (ag.agentDefId) {
                counts[ag.agentDefId] = (counts[ag.agentDefId] || 0) + 1;
              }
            });
          }

          // 4. Count user apps by userAppDefId
          if (Array.isArray(userApps)) {
            userApps.forEach((ua: any) => {
              if (ua.userAppDefId) {
                counts[ua.userAppDefId] = (counts[ua.userAppDefId] || 0) + 1;
              }
            });
          }

          setInstanceCounts(counts);
        } catch (err) {
          console.error('Failed to fetch instance counts', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch marketplace items', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainToggle = (domain: string) => {
    setSelectedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  const handleTechToggle = (tech: string) => {
    setSelectedTech((prev) =>
      prev.includes(tech) ? prev.filter((t) => t !== tech) : [...prev, tech]
    );
  };

  const clearFilters = () => {
    setSelectedDomains([]);
    setSelectedTech([]);
    setSearchQuery('');
    setSortBy('instances');
    setShowBookmarksOnly(false);
  };

  const toggleBookmark = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setBookmarks((prev) =>
      prev.includes(id) ? prev.filter((bId) => bId !== id) : [...prev, id]
    );
  };

  const handleShareItem = (item: MarketplaceItem) => {
    try {
      const shareUrl = `${window.location.origin}/marketplace?search=${encodeURIComponent(item.name)}`;
      navigator.clipboard.writeText(shareUrl);
      notificationContext.toast({
        message: `Shareable link for "${item.name}" copied to clipboard!`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to copy share url', err);
    }
  };

  // Client-side filtering & sorting
  const filteredItems = items
    .filter((item) => {
      // 1. Favorites Filter
      if (showBookmarksOnly && !bookmarks.includes(item.id)) return false;

      // 2. Category Filter
      if (activeCategory !== 'all') {
        if (activeCategory === 'blueprint' && item.type !== 'blueprint') return false;
        if (activeCategory === 'dataSourceDef' && item.type !== 'dataSourceDef') return false;
        if (activeCategory === 'agentDef' && item.type !== 'agentDef') return false;
        if (activeCategory === 'userAppDef' && item.type !== 'userAppDef') return false;
      }

      // 3. Search Query Filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchName = item.name?.toLowerCase().includes(q);
        const matchDesc = item.description?.toLowerCase().includes(q);
        if (!matchName && !matchDesc) return false;
      }

      // 4. Domain Filter (item must match at least one selected domain)
      if (selectedDomains.length > 0) {
        const itemDomains = item.metadata?.domains || [];
        const hasMatchingDomain = itemDomains.some((d) => 
          selectedDomains.some((sd) => sd.toLowerCase() === d.toLowerCase())
        );
        if (!hasMatchingDomain) return false;
      }

      // 5. Technology Filter (item must match at least one selected tech)
      if (selectedTech.length > 0) {
        const itemTechs = item.metadata?.tech || [];
        const hasMatchingTech = itemTechs.some((t) => 
          selectedTech.some((st) => st.toLowerCase() === t.toLowerCase())
        );
        if (!hasMatchingTech) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name_asc') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'name_desc') {
        return b.name.localeCompare(a.name);
      }
      if (sortBy === 'type') {
        return a.type.localeCompare(b.type);
      }
      if (sortBy === 'instances') {
        const countA = instanceCounts[a.id] || 0;
        const countB = instanceCounts[b.id] || 0;
        return countB - countA;
      }
      return 0;
    });

  const getIcon = (type: string) => {
    switch (type) {
      case 'blueprint':
        return <AccountTreeIcon sx={{ fontSize: 22 }} />;
      case 'dataSourceDef':
        return <StorageIcon sx={{ fontSize: 22 }} />;
      case 'agentDef':
        return <SmartToyIcon sx={{ fontSize: 22 }} />;
      case 'userAppDef':
        return <AppsIcon sx={{ fontSize: 22 }} />;
      default:
        return <AppsIcon sx={{ fontSize: 22 }} />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'blueprint':
        return 'Pipeline Blueprint';
      case 'dataSourceDef':
        return 'Data Source';
      case 'agentDef':
        return 'AI Agent';
      case 'userAppDef':
        return 'Application';
      default:
        return 'Template';
    }
  };

  const getThemeColor = (type: string) => {
    switch (type) {
      case 'blueprint':
        return theme.palette.primary.main;
      case 'dataSourceDef':
        return '#10b981';
      case 'agentDef':
        return '#8b5cf6';
      case 'userAppDef':
        return '#ec4899';
      default:
        return theme.palette.secondary.main;
    }
  };

  const handleUseItem = (item: MarketplaceItem) => {
    switch (item.type) {
      case 'blueprint':
        navigate(`/pipelineBuilder?blueprintId=${item.id}`);
        break;
      case 'dataSourceDef':
        navigate(`/createDataSourceInstance?defId=${item.id}`);
        break;
      case 'agentDef':
        navigate(`/createAgentInstance?defId=${item.id}`);
        break;
      case 'userAppDef':
        navigate(`/createUserAppInstance?defId=${item.id}`);
        break;
      default:
        break;
    }
  };

  // Search keyword highlighter component helper
  const HighlightText = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight.trim()) {
      return <>{text}</>;
    }
    const regex = new RegExp(`(${highlight.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <Box
              key={i}
              component="mark"
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.4),
                color: 'inherit',
                borderRadius: '2px',
                px: '2px',
                fontWeight: 700,
              }}
            >
              {part}
            </Box>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // Custom component details renderer inside Drawer
  const renderOriginalConfig = (item: MarketplaceItem) => {
    const orig = item.original || {};
    const themeColor = getThemeColor(item.type);

    switch (item.type) {
      case 'blueprint': {
        const nodes = orig.graph?.nodes || orig.nodes || [];
        const edges = orig.graph?.edges || orig.edges || orig.connections || [];
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Pipeline Architecture
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Total Nodes</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: themeColor }}>{nodes.length || 4}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Total Streams</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: themeColor }}>{edges.length || 3}</Typography>
                </Box>
              </Grid>
            </Grid>
            {nodes.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                  Components & Processors
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {nodes.map((node: any, idx: number) => (
                    <Chip
                      key={idx}
                      label={node.name || node.label || node.type || `Node ${idx + 1}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        borderColor: alpha(themeColor, 0.2),
                        bgcolor: alpha(themeColor, 0.02),
                        color: 'text.primary',
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        );
      }
      case 'dataSourceDef': {
        const adapter = orig.adapterType || orig.type || 'Standard';
        const properties = orig.configSchema?.properties || orig.properties || {};
        const propKeys = Object.keys(properties);
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Data Source Specifications
            </Typography>
            <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Adapter Type</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: themeColor }}>{adapter}</Typography>
            </Box>
            {propKeys.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                  Required Parameters
                </Typography>
                <Stack spacing={1}>
                  {propKeys.map((key) => {
                    const prop = properties[key];
                    return (
                      <Stack key={key} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '12px' }}>
                          {key}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {prop.type || 'string'}{prop.required ? ' *' : ''}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        );
      }
      case 'agentDef': {
        const model = orig.model || orig.llmModel || orig.llmConfig?.model || 'Gemini 1.5 Pro';
        const provider = orig.llmProvider || orig.provider || 'Google LLM';
        const instructions = orig.systemInstructions || orig.systemPrompt || orig.instructions;
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Agent Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>LLM Provider</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: themeColor }}>{provider}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Default Model</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: themeColor }}>{model}</Typography>
                </Box>
              </Grid>
            </Grid>
            {instructions && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  System Instructions
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {instructions}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        );
      }
      case 'userAppDef': {
        const views = orig.views || orig.dashboards || [];
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              App Setup
            </Typography>
            <Box sx={{ p: 2, bgcolor: alpha(themeColor, 0.05), borderRadius: '12px', border: `1px solid ${alpha(themeColor, 0.1)}` }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Application Layout</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: themeColor }}>
                {views.length || 1} Custom Dashboard{views.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        p: { xs: 1.5, sm: 3 },
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 2 },
        overflowY: 'auto',
      }}
    >
      {/* Clean, Polished Page Header */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          pb: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton
          onClick={() => navigate('/dashboard')}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            p: 0.75,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
            '&:hover': {
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              borderColor: theme.palette.secondary.main,
              transform: 'translateX(-2px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              color: 'text.primary',
              letterSpacing: '-0.5px',
              fontSize: { xs: '18px', sm: '22px', md: '24px' },
            }}
          >
            Integration Marketplace
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              mt: 0.25,
              fontSize: '12.5px',
              lineHeight: 1.3,
            }}
          >
            Discover and instantly deploy pre-configured pipelines, AI agents, and apps.
          </Typography>
        </Box>
      </Stack>

      {/* Responsive Two-Column Layout (Sidebar Filters + Results Area) */}
      <Grid container spacing={3} sx={{ flexGrow: 1, minHeight: 0 }}>
        
        {/* Left Column Sidebar Filters (xs: 12, md: 3) */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(12px)',
              borderRadius: '20px',
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.02)}`,
              position: { md: 'sticky' },
              top: '24px',
              maxHeight: { md: '72vh' },
              overflowY: { md: 'auto' },
              scrollbarWidth: 'thin',
              scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.primary.main, 0.25),
                borderRadius: '4px',
              },
            }}
          >
            {/* Search Input Box */}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '14px',
                      bgcolor: 'rgba(255, 255, 255, 0.65)',
                      fontSize: '13px',
                      height: '40px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.secondary.main,
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
                  size="small"
                  sx={{
                    p: 0.75,
                    color: showBookmarksOnly ? '#FFC107' : 'text.secondary',
                    bgcolor: 'rgba(255, 255, 255, 0.65)',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    height: '40px',
                    width: '40px',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  title={showBookmarksOnly ? "Show all resources" : "Filter by favorites"}
                >
                  {showBookmarksOnly ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Stack>
            </Box>

            {/* Detailed Filters (Collapsible on Mobile) */}
            {isMobile ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: 'transparent',
                  border: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                  sx={{
                    px: 0,
                    minHeight: 0,
                    '& .MuiAccordionSummary-content': { my: 0.5 },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    Detailed Filters
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, fontSize: '13px' }}>
                      Domains
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 0.5,
                        maxHeight: expandedDomains ? '160px' : 'none',
                        overflowY: expandedDomains ? 'auto' : 'visible',
                        pr: expandedDomains ? 0.5 : 0,
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.25),
                          borderRadius: '4px',
                        },
                      }}
                    >
                      {(expandedDomains ? domainsList : domainsList.slice(0, 6)).map((domain: string) => {
                        const isChecked = selectedDomains.includes(domain);
                        return (
                          <FormControlLabel
                            key={domain}
                            sx={{ mr: 0 }}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleDomainToggle(domain)}
                                size="small"
                                sx={{
                                  color: 'text.secondary',
                                  p: 0.25,
                                  '&.Mui-checked': {
                                    color: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                title={domain}
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: isChecked ? 700 : 500,
                                  color: isChecked ? 'text.primary' : 'text.secondary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {domain}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Box>
                    {domainsList.length > 6 && (
                      <Button
                        size="small"
                        onClick={() => setExpandedDomains(!expandedDomains)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          alignSelf: 'flex-start',
                          mt: 0.5,
                          p: 0,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        {expandedDomains ? 'Show Less' : `Show More (${domainsList.length - 6} more)`}
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ opacity: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, fontSize: '13px' }}>
                      Technologies
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 0.5,
                        maxHeight: expandedTech ? '160px' : 'none',
                        overflowY: expandedTech ? 'auto' : 'visible',
                        pr: expandedTech ? 0.5 : 0,
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.25),
                          borderRadius: '4px',
                        },
                      }}
                    >
                      {(expandedTech ? techList : techList.slice(0, 6)).map((tech: string) => {
                        const isChecked = selectedTech.includes(tech);
                        return (
                          <FormControlLabel
                            key={tech}
                            sx={{ mr: 0 }}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleTechToggle(tech)}
                                size="small"
                                sx={{
                                  color: 'text.secondary',
                                  p: 0.25,
                                  '&.Mui-checked': {
                                    color: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                title={tech}
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: isChecked ? 700 : 500,
                                  color: isChecked ? 'text.primary' : 'text.secondary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {tech}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Box>
                    {techList.length > 6 && (
                      <Button
                        size="small"
                        onClick={() => setExpandedTech(!expandedTech)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          alignSelf: 'flex-start',
                          mt: 0.5,
                          p: 0,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        {expandedTech ? 'Show Less' : `Show More (${techList.length - 6} more)`}
                      </Button>
                    )}
                  </Box>
                  {(selectedDomains.length > 0 || selectedTech.length > 0 || searchQuery || showBookmarksOnly || sortBy !== 'instances') && (
                    <>
                      <Divider sx={{ opacity: 0.5 }} />
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: '12px',
                          py: 1,
                          fontSize: '13px',
                          borderColor: theme.palette.divider,
                          color: 'text.primary',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: 'rgba(0,0,0,0.02)',
                          },
                        }}
                      >
                        Clear All Filters
                      </Button>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, fontSize: '13px' }}>
                    Domains
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 0.5,
                      maxHeight: expandedDomains ? '160px' : 'none',
                      overflowY: expandedDomains ? 'auto' : 'visible',
                      pr: expandedDomains ? 0.5 : 0,
                      scrollbarWidth: 'thin',
                      scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.25),
                        borderRadius: '4px',
                      },
                    }}
                  >
                    {(expandedDomains ? domainsList : domainsList.slice(0, 6)).map((domain: string) => {
                      const isChecked = selectedDomains.includes(domain);
                      return (
                        <FormControlLabel
                          key={domain}
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleDomainToggle(domain)}
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                p: 0.25,
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.main,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              title={domain}
                              sx={{
                                fontSize: '12px',
                                fontWeight: isChecked ? 700 : 500,
                                color: isChecked ? 'text.primary' : 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {domain}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Box>
                  {domainsList.length > 6 && (
                    <Button
                      size="small"
                      onClick={() => setExpandedDomains(!expandedDomains)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        p: 0,
                        minWidth: 0,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      {expandedDomains ? 'Show Less' : `Show More (${domainsList.length - 6} more)`}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ opacity: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, fontSize: '13px' }}>
                    Technologies
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 0.5,
                      maxHeight: expandedTech ? '160px' : 'none',
                      overflowY: expandedTech ? 'auto' : 'visible',
                      pr: expandedTech ? 0.5 : 0,
                      scrollbarWidth: 'thin',
                      scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.25),
                        borderRadius: '4px',
                      },
                    }}
                  >
                    {(expandedTech ? techList : techList.slice(0, 6)).map((tech: string) => {
                      const isChecked = selectedTech.includes(tech);
                      return (
                        <FormControlLabel
                          key={tech}
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleTechToggle(tech)}
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                p: 0.25,
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.main,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              title={tech}
                              sx={{
                                fontSize: '12px',
                                fontWeight: isChecked ? 700 : 500,
                                color: isChecked ? 'text.primary' : 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {tech}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Box>
                  {techList.length > 6 && (
                    <Button
                      size="small"
                      onClick={() => setExpandedTech(!expandedTech)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        p: 0,
                        minWidth: 0,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      {expandedTech ? 'Show Less' : `Show More (${techList.length - 6} more)`}
                    </Button>
                  )}
                </Box>
                {(selectedDomains.length > 0 || selectedTech.length > 0 || searchQuery || showBookmarksOnly || sortBy !== 'instances') && (
                  <>
                    <Divider sx={{ opacity: 0.5 }} />
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={clearFilters}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '12px',
                        py: 1,
                        fontSize: '13px',
                        borderColor: theme.palette.divider,
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Grid>

        {/* Right Column Results Grid & Header toolbar (xs: 12, md: 9) */}
        <Grid size={{ xs: 12, md: 9 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          
          {/* Main Top Header Toolbar (Category Tab buttons + Sort dropdown) */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(12px)',
              borderRadius: '20px',
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.02)}`,
            }}
          >
            {/* Horizontal Scrollable Category Pills */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: 'auto',
                width: { xs: '100%', sm: 'auto' },
                pb: { xs: 1, sm: 0 },
                maxWidth: '100%',
                '&::-webkit-scrollbar': { display: 'none' }, // clean look
              }}
            >
              {[
                { value: 'all', label: 'All Items' },
                { value: 'blueprint', label: 'Blueprints' },
                { value: 'dataSourceDef', label: 'Data Sources' },
                { value: 'agentDef', label: 'AI Agents' },
                { value: 'userAppDef', label: 'Applications' },
                { value: 'mcp', label: 'Smithery & MCP Tools' },
              ].map((category) => (
                <Button
                  key={category.value}
                  variant={activeCategory === category.value ? 'contained' : 'outlined'}
                  color={activeCategory === category.value ? 'secondary' : 'inherit'}
                  onClick={() => setActiveCategory(category.value)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '10px',
                    px: 2.25,
                    py: 0.75,
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    borderColor: activeCategory === category.value ? 'transparent' : 'divider',
                    color: activeCategory === category.value ? theme.palette.primary.main : 'text.primary',
                    '&:hover': {
                      boxShadow: 'none',
                      borderColor: activeCategory === category.value ? 'transparent' : theme.palette.secondary.main,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {category.label}
                </Button>
              ))}
            </Stack>

            {/* Sort By selector component */}
            <FormControl sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="sort-select-label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Sort By</InputLabel>
              <Select
                labelId="sort-select-label"
                id="sort-select"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as string)}
                sx={{
                  borderRadius: '12px',
                  bgcolor: 'rgba(255, 255, 255, 0.65)',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.secondary.main },
                  height: '42px',
                }}
              >
                <MenuItem value="instances" sx={{ fontSize: '12px' }}>Active Instances</MenuItem>
                <MenuItem value="name_asc" sx={{ fontSize: '12px' }}>Name: A-Z</MenuItem>
                <MenuItem value="name_desc" sx={{ fontSize: '12px' }}>Name: Z-A</MenuItem>
                <MenuItem value="type" sx={{ fontSize: '12px' }}>Resource Type</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Results count text label */}
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, fontWeight: 600 }}>
            {activeCategory === 'mcp'
              ? 'Smithery.ai & Model Context Protocol (MCP) Connectors'
              : `Showing ${filteredItems.length} matching resources ${showBookmarksOnly ? 'in Favorites' : ''}`}
          </Typography>

          {/* Items grid or Smithery MCP view */}
          <Box sx={{ flexGrow: 1 }}>
            {activeCategory === 'mcp' ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(12px)',
                  borderRadius: '24px',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: theme.palette.primary.main }}>
                  Smithery MCP Connectors & Tools
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Connect your interactive chat assistant and ingestion pipelines to thousands of tools published on the{' '}
                  <a href="https://smithery.ai" target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                    Smithery.ai Registry
                  </a>
                  . Discover capabilities, test arguments, and configure namespaces.
                </Typography>

                <McpToolViewer />
              </Paper>
            ) : loading ? (
              /* Skeleton Loader */
              <Grid container spacing={2}>
                {Array.from({ length: 6 }).map((_, idx) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={idx}>
                    <Card
                      sx={{
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.45)',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '18px',
                        p: 2.25,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Skeleton variant="text" width="65%" height={24} />
                          <Skeleton variant="text" width="35%" height={16} />
                        </Box>
                      </Stack>
                      <Skeleton variant="text" width="95%" height={16} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="85%" height={16} sx={{ mb: 2 }} />
                      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                        <Skeleton variant="rounded" width={55} height={18} sx={{ borderRadius: '9px' }} />
                        <Skeleton variant="rounded" width={65} height={18} sx={{ borderRadius: '9px' }} />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 'auto' }}>
                        <Skeleton variant="rounded" sx={{ width: { xs: '100%', sm: '35%' }, height: 36, borderRadius: '10px' }} />
                        <Skeleton variant="rounded" sx={{ width: { xs: '100%', sm: '65%' }, height: 36, borderRadius: '10px' }} />
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : filteredItems.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '340px',
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '24px',
                  border: '1px dashed',
                  borderColor: 'divider',
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SearchIcon sx={{ fontSize: 54, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  No integrations found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 380 }}>
                  We couldn't find anything matching your filters. Try clearing options or toggling favorites off.
                </Typography>
                <Button variant="outlined" color="primary" onClick={clearFilters} sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}>
                  Clear Filters
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredItems.map((item) => {
                  const brandColor = getThemeColor(item.type);
                  const isBookmarked = bookmarks.includes(item.id);
                  const instanceCount = instanceCounts[item.id] || 0;
                  return (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={item.id}>
                      <Card
                        className="glass-card-light"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: 'rgba(255, 255, 255, 0.45)',
                          borderColor: 'divider',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: 'none',
                          borderWidth: 1,
                          borderRadius: '18px',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `0 12px 30px ${alpha(brandColor, 0.12)}`,
                            borderColor: brandColor,
                            '& .item-icon-box': {
                              bgcolor: alpha(brandColor, 0.15),
                              transform: 'scale(1.08)',
                            },
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {/* Card Header Row */}
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              className="item-icon-box"
                              sx={{
                                p: 1.25,
                                borderRadius: '12px',
                                bgcolor: alpha(brandColor, 0.1),
                                color: brandColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease',
                              }}
                            >
                              {getIcon(item.type)}
                            </Box>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <HighlightText text={item.name} highlight={searchQuery} />
                              </Typography>
                              <Chip
                                label={getTypeLabel(item.type)}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '9px',
                                  fontWeight: 750,
                                  bgcolor: alpha(brandColor, 0.1),
                                  color: brandColor,
                                  border: `1px solid ${alpha(brandColor, 0.15)}`,
                                  mt: 0.25,
                                }}
                              />
                            </Box>
                            
                            {/* Favorite Heart Trigger */}
                            <IconButton
                              onClick={(e) => toggleBookmark(item.id, e)}
                              size="small"
                              sx={{
                                color: isBookmarked ? theme.palette.error.main : 'text.secondary',
                                bgcolor: isBookmarked ? alpha(theme.palette.error.main, 0.08) : 'transparent',
                                '&:hover': {
                                  bgcolor: isBookmarked ? alpha(theme.palette.error.main, 0.15) : 'rgba(0,0,0,0.04)',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                                ml: 'auto',
                              }}
                            >
                              {isBookmarked ? <FavoriteIcon sx={{ fontSize: 20 }} /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </Stack>

                          {/* Active Instances Count Row */}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                              variant="caption"
                              sx={{
                                color: instanceCount > 0 ? theme.palette.success.main : 'text.secondary',
                                bgcolor: instanceCount > 0 ? alpha(theme.palette.success.main, 0.08) : 'rgba(0,0,0,0.03)',
                                px: 1,
                                py: 0.25,
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '10px',
                              }}
                            >
                              {instanceCount} {instanceCount === 1 ? 'instance' : 'instances'} active
                            </Typography>
                          </Stack>

                          {/* Description */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              lineHeight: 1.45,
                              fontWeight: 500,
                              height: 38,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              my: 0.5,
                            }}
                          >
                            <HighlightText text={item.description} highlight={searchQuery} />
                          </Typography>

                          {/* Metadata Tags Row */}
                          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 'auto', pt: 1 }}>
                            {item.metadata?.domains?.map((d) => (
                              <Chip
                                key={d}
                                label={d}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '9px',
                                  height: 18,
                                  fontWeight: 700,
                                  borderColor: alpha(theme.palette.secondary.main, 0.3),
                                  color: theme.palette.primary.main,
                                  bgcolor: alpha(theme.palette.secondary.main, 0.02),
                                }}
                              />
                            ))}
                            {item.metadata?.tech?.map((t) => (
                              <Chip
                                key={t}
                                label={t}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '9px',
                                  height: 18,
                                  fontWeight: 700,
                                  borderColor: alpha(theme.palette.primary.main, 0.25),
                                  color: theme.palette.primary.main,
                                  bgcolor: alpha(theme.palette.primary.light, 0.25),
                                }}
                              />
                            ))}
                          </Stack>
                        </CardContent>

                        {/* Card Actions Bottom Row */}
                        <Box sx={{ px: 2.5, pb: 2.5 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button
                              variant="outlined"
                              onClick={() => setSelectedItem(item)}
                              sx={{
                                flex: 1,
                                width: '100%',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                                borderColor: 'divider',
                                color: 'text.primary',
                                fontSize: '0.8rem',
                                py: { xs: 1, sm: 0.75 },
                                '&:hover': {
                                  borderColor: brandColor,
                                  bgcolor: alpha(brandColor, 0.04),
                                },
                              }}
                            >
                              Details
                            </Button>
                            
                            <Button
                              variant="contained"
                              onClick={() => handleUseItem(item)}
                              sx={{
                                flex: 1.5,
                                width: '100%',
                                bgcolor: theme.palette.secondary.main,
                                color: theme.palette.primary.main,
                                fontWeight: 800,
                                textTransform: 'none',
                                borderRadius: '10px',
                                py: { xs: 1, sm: 0.75 },
                                fontSize: '0.8rem',
                                boxShadow: 'none',
                                '&:hover': {
                                  bgcolor: theme.palette.secondary.main,
                                  filter: 'brightness(0.9)',
                                  boxShadow: 'none',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {item.type === 'blueprint' ? 'Create Pipeline' : 'Create Instance'}
                            </Button>
                          </Stack>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Slide-out Technical Preview Drawer */}
      <Drawer
        anchor="right"
        open={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: `-10px 0 30px ${alpha(theme.palette.primary.main, 0.08)}`,
          },
        }}
      >
        {selectedItem && (
          <>
            {/* Drawer Header */}
            <Box
              sx={{
                p: 3,
                pb: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                background: `linear-gradient(135deg, ${alpha(getThemeColor(selectedItem.type), 0.06)} 0%, transparent 100%)`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  label={getTypeLabel(selectedItem.type)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '10px',
                    fontWeight: 800,
                    bgcolor: alpha(getThemeColor(selectedItem.type), 0.12),
                    color: getThemeColor(selectedItem.type),
                    border: `1px solid ${alpha(getThemeColor(selectedItem.type), 0.2)}`,
                  }}
                />
                <IconButton onClick={() => setSelectedItem(null)} size="small" sx={{ color: 'text.secondary' }}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    bgcolor: alpha(getThemeColor(selectedItem.type), 0.12),
                    color: getThemeColor(selectedItem.type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getIcon(selectedItem.type)}
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}>
                    {selectedItem.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: (instanceCounts[selectedItem.id] || 0) > 0 ? theme.palette.success.main : 'text.secondary',
                        bgcolor: (instanceCounts[selectedItem.id] || 0) > 0 ? alpha(theme.palette.success.main, 0.08) : 'rgba(0,0,0,0.03)',
                        px: 1,
                        py: 0.25,
                        borderRadius: '6px',
                        fontWeight: 700,
                        fontSize: '10px',
                      }}
                    >
                      {instanceCounts[selectedItem.id] || 0} {(instanceCounts[selectedItem.id] || 0) === 1 ? 'instance' : 'instances'} active
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Drawer Content */}
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Description */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                  About this Integration
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontWeight: 500 }}>
                  {selectedItem.description}
                </Typography>
              </Box>

              <Divider />

              {/* Tags & Environment compatibilities */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5 }}>
                  Target Environments & Tags
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.75 }}>
                      Compatible Domains
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {selectedItem.metadata?.domains?.map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          size="small"
                          sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            borderColor: alpha(theme.palette.secondary.main, 0.4),
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.75 }}>
                      Technology Stack
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {selectedItem.metadata?.tech?.map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.light, 0.5),
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Technical properties section */}
              {renderOriginalConfig(selectedItem)}
            </Box>

            {/* Drawer Footer Actions */}
            <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.01)' }}>
              <Stack direction="row" spacing={2}>
                <IconButton
                  onClick={(e) => toggleBookmark(selectedItem.id, e)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    color: bookmarks.includes(selectedItem.id) ? theme.palette.error.main : 'text.secondary',
                    bgcolor: bookmarks.includes(selectedItem.id) ? alpha(theme.palette.error.main, 0.05) : 'transparent',
                    '&:hover': {
                      bgcolor: bookmarks.includes(selectedItem.id) ? alpha(theme.palette.error.main, 0.1) : 'rgba(0,0,0,0.03)',
                      borderColor: bookmarks.includes(selectedItem.id) ? theme.palette.error.main : 'text.secondary',
                    },
                    borderRadius: '12px',
                    p: 1.5,
                  }}
                >
                  {bookmarks.includes(selectedItem.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>

                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => handleShareItem(selectedItem)}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '12px',
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: 'rgba(0,0,0,0.02)',
                    },
                  }}
                >
                  Share Link
                </Button>

                <Button
                  variant="contained"
                  onClick={() => {
                    handleUseItem(selectedItem);
                    setSelectedItem(null);
                  }}
                  sx={{
                    flex: 1.5,
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: '12px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: theme.palette.secondary.main,
                      filter: 'brightness(0.9)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {selectedItem.type === 'blueprint' ? 'Create Pipeline' : 'Create Instance'}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default MarketplacePage;
